/** Reads the corroboration current-incident read model (GET /incidents?cells=) and
 *  adapts it into synthetic app.aid.incident.snapshot events so the existing
 *  IncidentStore folds the viewport seed exactly like live snapshots. Read-only. */

/** One incident from GET /incidents — mirrors the Go IncidentView JSON: FLAT
 *  category/event/severity (NOT a snapshot envelope's nested classification). */
export interface ReadModelIncident {
  subject: string;
  category?: string;
  event?: string;
  severity?: string; // CAP severity
  status: string;
  W?: number;
  confidence?: number;
  cells?: string[];
  source?: string;
  distinctIdentities?: number;
  contributors?: { feed?: string; identity?: string; itemId?: string }[];
  footprint?: string[];
  openedAt?: number; // micros
  lastConfirmedAt?: string; // ISO
  currentFrpMw?: number;
  peakFrpMw?: number;
  trend?: { intensity: string; footprint: string; corroboration: string; worsening: boolean };
}

export interface IncidentsClientOptions {
  baseUrl: string;
  fetchImpl?: typeof fetch;
}

export class IncidentsClient {
  constructor(private readonly opts: IncidentsClientOptions) {}
  private get fetch(): typeof fetch {
    return this.opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  /** Current open incidents whose cells intersect `cells` (O(open), no history). */
  async incidentsInCells(cells: string[]): Promise<ReadModelIncident[]> {
    if (cells.length === 0) return [];
    const res = await this.fetch(`${this.opts.baseUrl}/incidents?cells=${encodeURIComponent(cells.join(","))}`);
    if (!res.ok) throw new Error(`incidents fetch failed: ${res.status} ${res.statusText}`);
    const body = (await res.json()) as { incidents?: ReadModelIncident[] };
    return body.incidents ?? [];
  }
}

function microsToIso(micros?: number): string {
  // wallclock-allow: formats a GIVEN instant, reads no clock (gate-30 excludes apps/webapp).
  if (!micros || micros <= 0) return "";
  return new Date(Math.floor(micros / 1000)).toISOString();
}

/** Convert read-model incidents to synthetic app.aid.incident.snapshot events. seq 0 ⇒
 *  any live-tailed snapshot for the same subject wins (IncidentStore latest-wins by seq).
 *  Pure. */
export function seedEventsFromReadModel(
  views: ReadModelIncident[],
): Array<{ seq: number; json: Record<string, unknown> }> {
  return views.map((v) => ({
    seq: 0,
    json: {
      $type: "app.aid.incident.snapshot",
      createdAt: v.lastConfirmedAt ?? microsToIso(v.openedAt),
      geo: { cell: v.cells?.[0] ?? "" },
      payload: {
        subject: v.subject,
        classification: { category: v.category, event: v.event, severity: v.severity },
        status: v.status,
        W: v.W,
        confidence: v.confidence,
        cells: v.cells ?? [],
        distinctIdentities: v.distinctIdentities ?? 0,
        source: v.source,
        openedAt: microsToIso(v.openedAt), // stable first-seen anchor (matches live snapshots)
        lastConfirmedAt: v.lastConfirmedAt,
        currentFrpMw: v.currentFrpMw,
        peakFrpMw: v.peakFrpMw,
        trend: v.trend,
        contributors: v.contributors,
        footprint: v.footprint,
      },
    },
  }));
}
